/* eslint-disable max-lines */
import { useCallback } from 'react';
import { convertFromRaw } from 'draft-js';
import { isNil, isNull, omit } from 'lodash-es';

import {
  JOB_TYPE,
  JobApplicationMethodOption,
} from '../../../common/constants';
import { countryCodeIsVietnam } from '../../../common/helpers/countryCodeHelper';
import { useGetCurrentCompanyJobSlotsSubscription } from '../../../common/hooks/useGetCurrentCompanyJobSlotsSubscription';
import { getFeatureEnabledForCountry } from '../../../common/hooks/useGetFeatureEnabledForCountry';
import { useGetSessionCompany } from '../../../common/hooks/useGetSessionCompany';
import useIsWorkAddressEnabled from '../../../common/hooks/useIsWorkAddressEnabled';
import { Job } from '../../../common/interfaces/entities';
import { JobResponse } from '../../../common/interfaces/responses';
import { useGlintsVIPMembershipInfo } from '../../../components/GlintsVIPMembershipInfo/hooks/useGlintsVIPMembershipInfo';
import {
  BonusFrequency,
  DEFAULT_JOB_STATUS,
  DEFAULT_NUMBER_OF_HIRES,
  GenderPreferenceForJob,
  getMinAgeForCountry,
  JobDraftFormState,
  JobDraftPostingPayload,
  JobFormState,
  JobPostingPayload,
  JobStatus,
  SalaryBasic,
  SalaryBonus,
  SalaryType,
  Seniority,
} from '../common/constants';
import {
  getHierarchicalLocationPrefill,
  isWorkAddressEnabledForJob,
} from '../common/helper';
import { getSanitizedSalaryState } from './getSalaryStateFromResponse';
import {
  convertScreeningQuestionFormStateToPayload,
  convertScreeningQuestionResponseToFormState,
} from './screeningQuestionsHelpers';
import useGetVIPJobFieldsDefaultValue from './useGetVIPJobFieldsDefaultValue';

export const yearsOfExperienceToSeniority = (
  min: number,
  max: number
): Seniority | undefined => {
  if (min === 0 && max === 1) return Seniority.LESS_THAN_A_YEAR;
  if (min === 1 && max === 3) return Seniority.ONE_TO_THREE;
  if (min === 3 && max === 5) return Seniority.THREE_TO_FIVE;
  if (min === 5 && max === 10) return Seniority.FIVE_TO_TEN;
  if (min === 10 && max === 50) return Seniority.TEN_PLUS;
  if (min === 0 && max === 50) return Seniority.NO_PREFERENCE;
  return undefined;
};

export const seniorityToYearsOfExperience = (
  seniority: Seniority
): { minYearsOfExperience: number; maxYearsOfExperience: number } =>
  ({
    [Seniority.LESS_THAN_A_YEAR]: {
      minYearsOfExperience: 0,
      maxYearsOfExperience: 1,
    },
    [Seniority.ONE_TO_THREE]: {
      minYearsOfExperience: 1,
      maxYearsOfExperience: 3,
    },
    [Seniority.THREE_TO_FIVE]: {
      minYearsOfExperience: 3,
      maxYearsOfExperience: 5,
    },
    [Seniority.FIVE_TO_TEN]: {
      minYearsOfExperience: 5,
      maxYearsOfExperience: 10,
    },
    [Seniority.TEN_PLUS]: {
      minYearsOfExperience: 10,
      maxYearsOfExperience: 50,
    },
    [Seniority.NO_PREFERENCE]: {
      minYearsOfExperience: 0,
      maxYearsOfExperience: 50,
    },
  })[seniority];

const useConvertJobData = () => {
  const { sessionCompany } = useGetSessionCompany();
  const CompanyId = sessionCompany?.id || '';

  const { vipMembershipInfo } = useGlintsVIPMembershipInfo();
  const { vipJobFieldsDefaultValue } = useGetVIPJobFieldsDefaultValue();
  const { currentCompanyJobSlotsSubscriptionData } =
    useGetCurrentCompanyJobSlotsSubscription();
  const isWorkAddressEnabled = useIsWorkAddressEnabled();

  const convertJobFormStateToPayload = useCallback(
    (
      jobFormState: JobFormState | JobDraftFormState
    ): JobPostingPayload | JobDraftPostingPayload => {
      const { isVIPMembershipEnabled, isSalaryVisibleForJob } =
        getFeatureEnabledForCountry(jobFormState.CountryCode);
      const candidateMustHaveFields = (function () {
        if (jobFormState.isEdit || !isVIPMembershipEnabled) {
          return {};
        }

        const mustHaveFields = {
          isLocationRequiredForCandidate: Boolean(
            jobFormState.isLocationRequiredForCandidate
          ),
          isEducationRequiredForCandidate: Boolean(
            jobFormState.isEducationRequiredForCandidate
          ),
        };

        return {
          ...mustHaveFields,
          isResumeRequiredForCandidate: Boolean(
            jobFormState.isResumeRequiredForCandidate
          ),
          vipCandidateInformation: {
            required: {
              profilePic: jobFormState.isProfilePicRequiredForCandidate,
            },
          },
        };
      })();

      const shouldShowSalary = (function () {
        const isCompanyCountryAndJobCountryVN = [
          sessionCompany?.CountryCode || '',
          jobFormState.CountryCode || '',
        ].every(countryCodeIsVietnam);

        const canShowSalary = isSalaryVisibleForJob && !jobFormState.isEdit;

        if (canShowSalary || isCompanyCountryAndJobCountryVN) {
          return {
            shouldShowSalary: jobFormState.shouldShowSalary,
          };
        }

        return {};
      })();

      const salaries: [SalaryBasic?, SalaryBonus?] = [];
      if (jobFormState?.minSalaryAmount && jobFormState?.CurrencyCode) {
        const newSalary = {
          salaryType: SalaryType.BASIC,
          salaryMode: jobFormState?.paymentFrequency
            ? jobFormState.salaryRate || jobFormState.salaryMode
            : BonusFrequency.MONTH,
          minAmount: jobFormState.minSalaryAmount,
          CurrencyCode: jobFormState.CurrencyCode,
          maxAmount: jobFormState?.maxSalaryAmount || null,
          ...(jobFormState?.paymentFrequency && {
            paymentFrequency: jobFormState.paymentFrequency,
          }),
        };
        salaries.push(newSalary as SalaryBasic);
      }

      if (
        jobFormState?.CurrencyCode &&
        jobFormState?.addBonus &&
        typeof jobFormState?.minBonusAmount !== 'undefined' &&
        jobFormState?.minBonusAmount !== null &&
        jobFormState?.maxBonusAmount &&
        jobFormState?.minBonusAmount < jobFormState?.maxBonusAmount
      ) {
        salaries.push({
          salaryType: SalaryType.BONUS,
          salaryMode: jobFormState?.salaryMode,
          minAmount: jobFormState?.minBonusAmount,
          maxAmount: jobFormState?.maxBonusAmount,
          CurrencyCode: jobFormState?.CurrencyCode,
        } as SalaryBonus);
      }

      const { isHierarchicalLocationEnabled } = getFeatureEnabledForCountry(
        jobFormState.CountryCode
      );

      const keywordMatchCriteria = (function () {
        if (!jobFormState.keywordMatchCriteria?.questions?.length) {
          return null;
        }

        const questionGroupId =
          jobFormState.keywordMatchCriteria.questions[0].questionGroupId;

        return {
          keywordMatchCriteria: {
            questionGroupId,
            keywords: jobFormState.keywordMatchCriteria.answers,
          },
        };
      })();

      return {
        status: DEFAULT_JOB_STATUS as JobStatus,
        CompanyId,
        numberOfHires: DEFAULT_NUMBER_OF_HIRES,
        receiverIds: jobFormState.receiverIds,
        ...(jobFormState.seniority &&
          seniorityToYearsOfExperience(jobFormState.seniority)),
        ...(keywordMatchCriteria
          ? {}
          : {
              skills: jobFormState?.skills?.map(skill => ({
                id: skill.id,
                mustHave: true,
              })),
            }),
        title: jobFormState.title,
        type: jobFormState.type,
        HierarchicalJobCategoryId: jobFormState.HierarchicalJobCategoryId,
        JobCategoryId: jobFormState.JobCategoryId,
        workArrangementOption: jobFormState.workArrangementOption,
        ...(isWorkAddressEnabledForJob(
          isWorkAddressEnabled,
          jobFormState.CountryCode || ''
        )
          ? {
              CompanyAddressId:
                jobFormState.companyAddress?.status === 'ACTIVE'
                  ? jobFormState.companyAddress.id
                  : null,
            }
          : jobFormState.CountryCode && isHierarchicalLocationEnabled
            ? { DistrictId: jobFormState.districtLocation?.id }
            : {
                CityId: jobFormState.CityId,
                DistrictId: null,
              }),
        CountryCode: jobFormState.CountryCode,
        acceptsForeignApplications: jobFormState.acceptsForeignApplications,
        descriptionRaw: jobFormState.descriptionRaw,
        salaries: salaries as [SalaryBasic, SalaryBonus?],
        ...(jobFormState.showWorkDetails && {
          metadata: {
            workShifts: jobFormState.workShifts,
            workingDaysPerWeek: jobFormState.workingDaysPerWeek,
            requiresWeekendHolidays:
              jobFormState.requiresWeekendHolidays ?? false,
          },
        }),
        interviewProcessRaw: jobFormState.interviewProcess
          ? jobFormState.interviewProcessRaw
          : null,
        shouldPublishOnLinkedin: jobFormState.shouldPublishOnLinkedin,
        benefits: jobFormState.benefits?.map(benefit => ({
          benefit: benefit.benefit,
          description:
            benefit.benefit === 'USER_DEFINED' ? benefit.title : undefined,
        })),
        shouldShowBenefits: true,
        educationLevel: jobFormState.educationLevel,
        CreatorId: jobFormState.CreatorId,
        minAge: jobFormState.noAgeLimit ? null : jobFormState.minAge ?? null,
        maxAge: jobFormState.noAgeLimit ? null : jobFormState.maxAge ?? null,
        gender:
          jobFormState.gender === GenderPreferenceForJob.NO_PREFERENCE
            ? null
            : jobFormState.gender,
        jobDraftId: jobFormState.jobDraftId,
        ...candidateMustHaveFields,
        ...shouldShowSalary,
        ...convertScreeningQuestionFormStateToPayload({
          screeningQuestions: jobFormState.screeningQuestions,
          isEdit: Boolean(jobFormState.isEdit),
          isVIPActive: vipMembershipInfo.isActive,
        }),
        externalApplyURL:
          jobFormState.jobApplicationMethodOption ===
          JobApplicationMethodOption.EXTERNAL
            ? jobFormState.externalApplyURL
            : null,
        ...keywordMatchCriteria,
      };
    },
    [
      CompanyId,
      isWorkAddressEnabled,
      sessionCompany?.CountryCode,
      vipMembershipInfo.isActive,
    ]
  );

  const getLocationStateFromResponse = useCallback(
    (response: JobResponse, isEdit = false) => {
      const companyAddress = response.data.companyAddress;
      if (
        companyAddress &&
        isWorkAddressEnabledForJob(
          isWorkAddressEnabled,
          response.data.CountryCode
        )
      ) {
        return {
          companyAddress:
            isEdit || companyAddress.status === 'ACTIVE'
              ? companyAddress
              : null,
        };
      }

      const jobLocation = response.data?.location;
      const districtId = response.data?.DistrictId;

      if (districtId) {
        return {
          ...getHierarchicalLocationPrefill(jobLocation, districtId),
          originalDistrictId: districtId,
        };
      }

      if (jobLocation) {
        return getHierarchicalLocationPrefill(jobLocation, districtId);
      }

      return {};
    },
    [isWorkAddressEnabled]
  );

  const convertResponseToFormState = useCallback(
    (
      response: JobResponse,
      options?: { isEdit?: boolean; isCopy?: boolean }
    ): JobFormState | JobDraftFormState => {
      let jobData = response.data;

      const haveUnfilteredSkills = jobData.unfilteredSkills
        ? jobData.skills.length !== jobData.unfilteredSkills.length
        : false;

      const skillsData = !haveUnfilteredSkills
        ? jobData.skills?.map(skill => skill.skill)
        : [];

      jobData = {
        ...jobData,
        descriptionRaw: (jobData as Job).descriptionJsonString
          ? JSON.parse((jobData as Job).descriptionJsonString || '')
          : null,
        interviewProcessRaw: (jobData as Job).interviewProcessJsonString
          ? JSON.parse((jobData as Job).interviewProcessJsonString || '')
          : null,
      };

      const isCompanyCountryAndJobCountryVN = [
        sessionCompany?.CountryCode || '',
        jobData.CountryCode || '',
      ].every(countryCodeIsVietnam);

      const shouldResetVIPFieldsValues =
        !vipMembershipInfo.isActive && !isCompanyCountryAndJobCountryVN;

      const vipJobFieldsDefaultValueForFormState = {
        ...omit(vipJobFieldsDefaultValue, 'vipCandidateInformation'),
        isProfilePicRequiredForCandidate: Boolean(
          vipJobFieldsDefaultValue?.vipCandidateInformation?.required
            ?.profilePic
        ),
        screeningQuestionsEnabled: jobData.screeningQuestionsEnabled,
      };

      const shouldClearAge =
        options?.isCopy &&
        !isNil(jobData.minAge) &&
        jobData.minAge < getMinAgeForCountry(jobData.CountryCode);

      const metadata = jobData.metadata;

      const locationStateFromResponse = getLocationStateFromResponse(
        response,
        options?.isEdit && !options?.isCopy
      ) as Pick<JobFormState, 'companyAddress' | 'cityLocation'>;

      const formStateToSet = {
        title: jobData.title || '',
        jobTypeSuggestionTitle: jobData.title || null,
        userChangedJobType: false,
        type:
          jobData.type === null || jobData.type === undefined
            ? null
            : Object.values(JOB_TYPE).includes(jobData.type)
              ? jobData.type
              : '',
        HierarchicalJobCategoryId: jobData.HierarchicalJobCategoryId,
        originalHierarchicalJobCategoryId: options?.isCopy
          ? null
          : jobData.HierarchicalJobCategoryId,
        originalLocationId: options?.isCopy
          ? null
          : locationStateFromResponse.companyAddress?.locationId ||
            locationStateFromResponse.cityLocation?.id ||
            null,
        HierarchicalJobCategoryName: jobData.HierarchicalJobCategoryId
          ? (jobData as Job).hierarchicalJobCategory?.leaves?.[0]?.name
          : null,
        categoryName: jobData.HierarchicalJobCategoryId
          ? (jobData as Job).hierarchicalJobCategory?.leaves?.[0]?.name
          : null,
        ...locationStateFromResponse,
        workArrangementOption: jobData.workArrangementOption,
        CityId: jobData.CityId,
        CountryCode: jobData.CountryCode,
        CreatorId: jobData.CreatorId,
        originalCreatorId: jobData.CreatorId,
        countryName: jobData.country?.name,
        isWhitelistedCountry: jobData.country
          ? jobData.country.isWhitelisted
          : true,
        acceptsForeignApplications: jobData.acceptsForeignApplications,
        description: jobData?.descriptionRaw
          ? convertFromRaw(jobData.descriptionRaw)
          : undefined,
        descriptionRaw: jobData.descriptionRaw,
        ...(!isNull(jobData?.minYearsOfExperience) &&
          !isNull(jobData?.maxYearsOfExperience) && {
            seniority: yearsOfExperienceToSeniority(
              jobData.minYearsOfExperience,
              jobData.maxYearsOfExperience
            ),
          }),
        skills: skillsData ?? [],
        shouldPublishOnLinkedin: jobData.shouldPublishOnLinkedin,
        ...getSanitizedSalaryState(response, jobData.type as JOB_TYPE),
        workShifts: metadata?.workShifts ?? null,
        workingDaysPerWeek: metadata?.workingDaysPerWeek ?? null,
        requiresWeekendHolidays: metadata?.requiresWeekendHolidays ?? false,
        showWorkDetails: Boolean(metadata?.workShifts?.length),
        benefits: jobData.benefits,
        receiverIds: Array.from(new Set(jobData.receiverIds)),
        interviewProcess: jobData.interviewProcessRaw
          ? convertFromRaw(jobData.interviewProcessRaw)
          : undefined,
        interviewProcessRaw: jobData.interviewProcessRaw,
        educationLevel: jobData.educationLevel,
        noAgeLimit: isNil(jobData.minAge) && isNil(jobData.maxAge),
        ...(shouldClearAge
          ? {
              minAge: null,
              maxAge: null,
            }
          : {
              minAge: jobData.minAge ?? null,
              maxAge: jobData.maxAge ?? null,
            }),
        gender: (jobData.gender ||
          GenderPreferenceForJob.NO_PREFERENCE) as GenderPreferenceForJob,
        unfilteredSkills: jobData.unfilteredSkills
          ? jobData.unfilteredSkills.map(skill => skill.skill)
          : [],
        haveUnfilteredSkills,
        isLocationRequiredForCandidate: Boolean(
          jobData.isLocationRequiredForCandidate
        ),
        isEducationRequiredForCandidate: Boolean(
          jobData.isEducationRequiredForCandidate
        ),
        isResumeRequiredForCandidate: Boolean(
          jobData.isResumeRequiredForCandidate
        ),
        screeningQuestions: convertScreeningQuestionResponseToFormState({
          screeningQuestions: jobData.screeningQuestions,
          isVIPActive: vipMembershipInfo.isActive,
        }),
        screeningQuestionsEnabled: jobData.screeningQuestionsEnabled,
        isProfilePicRequiredForCandidate: Boolean(
          jobData.vipCandidateInformation?.required?.profilePic
        ),
        jobApplicationMethodOption:
          !jobData.externalApplyURL ||
          (options?.isCopy &&
            !currentCompanyJobSlotsSubscriptionData?.hasJobExternalUrl)
            ? JobApplicationMethodOption.GLINTS
            : JobApplicationMethodOption.EXTERNAL,
        externalApplyURL: jobData.externalApplyURL ?? null,
        keywordMatchCriteria: {
          questions: jobData.keywordMatchCriteria?.questions ?? [],
          answers: jobData.keywordMatchCriteria?.answers ?? [],
        },
      };

      return {
        ...formStateToSet,
        ...(shouldResetVIPFieldsValues && vipJobFieldsDefaultValueForFormState),
      };
    },
    [
      getLocationStateFromResponse,
      sessionCompany?.CountryCode,
      vipJobFieldsDefaultValue,
      vipMembershipInfo.isActive,
      currentCompanyJobSlotsSubscriptionData?.hasJobExternalUrl,
    ]
  );

  const convertResponseToPayload = useCallback(
    (response: JobResponse): JobPostingPayload | JobDraftPostingPayload =>
      convertJobFormStateToPayload(convertResponseToFormState(response)),
    [convertJobFormStateToPayload, convertResponseToFormState]
  );

  return {
    convertJobFormStateToPayload,
    convertResponseToFormState,
    convertResponseToPayload,
  };
};

export default useConvertJobData;
